import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { Snackbar, Alert, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';

// Context für globale Snackbar-Verwaltung
const SnackbarContext = createContext();

// Hook zum Verwenden der Snackbar
export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar muss innerhalb von SnackbarProvider verwendet werden');
  }
  return context;
};

// Provider-Komponente
export const SnackbarProvider = ({ children }) => {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    type: 'info', // 'success', 'error', 'warning', 'info'
    errorCode: null,
    autoHideDuration: 5000
  });

  const [isHovering, setIsHovering] = useState(false);
  const hoverTimeoutRef = useRef(null);

  const showSnackbar = useCallback((message, type = 'info', errorCode = null) => {
    setSnackbar({
      open: true,
      message,
      type,
      errorCode,
      autoHideDuration: 5000
    });
  }, []);

  const showSuccess = useCallback((message) => {
    showSnackbar(message, 'success');
  }, [showSnackbar]);

  const showError = useCallback((message, errorCode = null) => {
    showSnackbar(message, 'error', errorCode);
  }, [showSnackbar]);

  const showWarning = useCallback((message) => {
    showSnackbar(message, 'warning');
  }, [showSnackbar]);

  const showInfo = useCallback((message) => {
    showSnackbar(message, 'info');
  }, [showSnackbar]);

  const handleClose = (event, reason) => {
    // Nicht schließen bei Klick außerhalb
    if (reason === 'clickaway') {
      return;
    }
    // Nur schließen wenn nicht hovering
    if (!isHovering) {
      setSnackbar(prev => ({ ...prev, open: false }));
    }
  };

  const handleMouseEnter = () => {
    // Timeout clearen falls vorhanden
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    // Nach Verlassen des Hovers automatisch nach kurzer Zeit schließen
    hoverTimeoutRef.current = setTimeout(() => {
      setSnackbar(prev => ({ ...prev, open: false }));
    }, 1000);
  };

  const { t } = useTranslation();

  return (
    <SnackbarContext.Provider
      value={{
        showSnackbar,
        showSuccess,
        showError,
        showWarning,
        showInfo
      }}
    >
      {children}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={isHovering ? null : snackbar.autoHideDuration}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        disableWindowBlurListener={true}
        sx={{
          '& .MuiSnackbarContent-root': {
            minWidth: '300px'
          }
        }}
      >
        <Box
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.type}
            variant="filled"
            className={`central-snackbar central-snackbar-${snackbar.type}`}
            sx={{
              width: '100%',
              minWidth: '300px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              fontSize: '0.95rem',
              fontWeight: 500
            }}
          >
            <Box>
              {snackbar.message}
              {snackbar.errorCode && (
                <Box
                  component="span"
                  sx={{
                    display: 'block',
                    fontSize: '0.8rem',
                    marginTop: '4px',
                    opacity: 0.9,
                    fontFamily: 'monospace'
                  }}
                >
                  {t('error.code', 'Fehlercode')}: {snackbar.errorCode}
                </Box>
              )}
            </Box>
          </Alert>
        </Box>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};


